/** @jsxImportSource preact */
import { h, ComponentChildren } from 'preact';
import { useEffect, useCallback, useRef } from 'preact/hooks';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ComponentChildren;
  width?: string;
}

// Guard window: after the modal opens we ignore backdrop clicks for this
// many ms. Stops the touch "ghost click" that fires on the backdrop right
// after a tap on a chart bar opens the modal — the same touch produces a
// synthetic click event at the touchend coordinates, which by then are
// covered by the modal we just opened.
const GHOST_CLICK_GRACE_MS = 350;

export function Modal({ isOpen, onClose, title, children, width = 'min(900px, 90vw)' }: ModalProps) {
  const openedAtRef = useRef(0);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      openedAtRef.current = Date.now();
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  const handleBackdropClick = () => {
    if (Date.now() - openedAtRef.current < GHOST_CLICK_GRACE_MS) return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '72px 12px 16px',
        overflowY: 'auto',
        zIndex: 2147483647,
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          background: 'var(--gc-surface, #f8fafc)',
          color: 'var(--gc-foreground, inherit)',
          borderRadius: 12,
          padding: 16,
          width,
          maxHeight: 'calc(100dvh - 88px)',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
            <button
              type="button"
              onClick={onClose}
              style={{
                border: '1px solid var(--gc-control-border, #d1d5db)',
                background: 'var(--gc-button-bg, #ffffff)',
                color: 'var(--gc-button-text, #111827)',
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 12,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Close
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
